// src/services/api.js
import axios from 'axios';

const api = axios.create({ baseURL: '/' });
// backend は SimpleJWT: /api/auth/token/refresh/
const API_REFRESH_URL = '/api/auth/token/refresh/';

function getStoredToken(key) {
  return localStorage.getItem(key) ?? sessionStorage.getItem(key) ?? null;
}

function setStoredToken(key, value) {
  if (localStorage.getItem('refresh')) {
    localStorage.setItem(key, value);
  } else {
    sessionStorage.setItem(key, value);
  }
}

api.interceptors.request.use((config) => {
  const access = getStoredToken('access');
  if (access) config.headers.Authorization = `Bearer ${access}`;
  config.headers['Cache-Control'] = 'no-cache';
  config.headers.Pragma = 'no-cache';
  return config;
});

// 401 時に refresh して1回だけ再試行（管理画面の axios がトークン切れで連続401になるのを防ぐ）
let refreshing = false;
let waiters = [];

async function refreshAccessToken() {
  const refresh = getStoredToken('refresh');
  if (!refresh) throw new Error('NO_REFRESH');
  const res = await axios.post(API_REFRESH_URL, { refresh });
  const { access } = res.data || {};
  if (!access) throw new Error('REFRESH_FAILED');
  setStoredToken('access', access);
  return access;
}

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const { config, response } = error || {};
    if (!response || response.status !== 401 || config.__retried) {
      return Promise.reject(error);
    }
    try {
      if (refreshing) {
        await new Promise((resolve) => waiters.push(resolve));
      } else {
        refreshing = true;
        await refreshAccessToken();
        refreshing = false;
        waiters.forEach((r) => r());
        waiters = [];
      }
      config.__retried = true;
      const t = getStoredToken('access');
      if (t) config.headers.Authorization = `Bearer ${t}`;
      return api.request(config);
    } catch (e) {
      refreshing = false;
      waiters.forEach((r) => r());
      waiters = [];
      ['access', 'refresh'].forEach((key) => {
        localStorage.removeItem(key);
        sessionStorage.removeItem(key);
      });
      return Promise.reject(error);
    }
  }
);

export default api;

const handleTokenRefresh = async () => {
    const refreshToken = localStorage.getItem('refresh') || sessionStorage.getItem('refresh');

    if (!refreshToken) {
        localStorage.removeItem('access');
        sessionStorage.removeItem('access');
        window.location.href = '/login';
        throw new Error('Refresh token not found. Logging out.');
    }

    const persist = !!localStorage.getItem('refresh');

    try {
        const refreshResponse = await fetch(API_REFRESH_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refresh: refreshToken }),
            credentials: 'include',
        });

        if (!refreshResponse.ok) {
            throw new Error('Refresh failed.');
        }

        const tokens = await refreshResponse.json();
        if (persist) {
            localStorage.setItem('access', tokens.access);
            if (tokens.refresh) localStorage.setItem('refresh', tokens.refresh);
        } else {
            sessionStorage.setItem('access', tokens.access);
            if (tokens.refresh) sessionStorage.setItem('refresh', tokens.refresh);
        }

        return tokens.access;

    } catch (error) {
        ['access', 'refresh'].forEach((k) => {
            localStorage.removeItem(k);
            sessionStorage.removeItem(k);
        });
        window.location.href = '/login';
        throw new Error('Failed to refresh token, forcing logout.');
    }
};

// --- 3. FETCH  ---

const tryFetch = async (url, token) => {
    const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`, 
    };

    const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: headers, 
    });

    if (response.status === 401) {
        return response; 
    }
    
    if (!response.ok) {
        const errorDetail = await response.json();
        throw new Error(JSON.stringify(errorDetail));
    }

    return response.json();
};

/*
export async function apiGet(url) {
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}
*/
export async function apiGet(url) {
    const token = localStorage.getItem('access') || sessionStorage.getItem('access');

    const headers = {
        'Content-Type': 'application/json',
    };

    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: headers, 
    });

    if (!response.ok) {
        const errorDetail = await response.json();
        if (response.status === 401) {
            console.error("Token が正しくません...");
            
        }
        throw new Error(JSON.stringify(errorDetail));
    }
    
    return response.json();
}

function getCookie(name) {
  const v = document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)');
  return v ? v.pop() : '';
}

/*
export async function apiPostJSON(url, body) {
  const csrftoken = getCookie('csrftoken');
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRFToken': csrftoken || ''
    },

    body: JSON.stringify(body)
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}
*/
export async function apiPostJSON(url, body) {
    const accessToken = localStorage.getItem('access') || sessionStorage.getItem('access');
    const csrftoken = getCookie('csrftoken');
    
    const headers = {
        'Content-Type': 'application/json',
        'X-CSRFToken': csrftoken || ''
    };
    
    if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`; 
    }
    
    const res = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: headers,
        body: JSON.stringify(body)
    });
    
    if (!res.ok) {
        const errorText = await res.text();
        throw new Error(errorText);
    }
    return res.json();
}